// Static Web Apps exposes the signed-in user at /.auth/me. Role gating is enforced
// server-side (staticwebapp.config.json + the BFF); this is only used to shape the UI.
import { i18n } from '@/i18n'
import { clearUserData } from '@/lib/offlineStorage'

export interface ClientPrincipal {
  userId: string
  userDetails: string
  identityProvider: string
  userRoles: string[]
  claims?: { typ: string; val: string }[]
}

function claim(p: ClientPrincipal, ...types: string[]): string | undefined {
  return p.claims?.find((c) => types.includes(c.typ) && c.val)?.val
}

/** Full name from the login provider's claims (Google/Facebook/Entra all supply it). */
export function userName(p: ClientPrincipal): string {
  const full = claim(p, 'name', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name')
  if (full) return full
  const given = claim(p, 'given_name')
  const family = claim(p, 'family_name')
  if (given || family) return [given, family].filter(Boolean).join(' ')
  // nameClaimType is "name", so userDetails is the name for our providers.
  return p.userDetails ?? ''
}

/** Email from the login provider's claims. */
export function userEmail(p: ClientPrincipal): string {
  return (
    claim(p, 'email', 'emails', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress') ??
    (p.userDetails?.includes('@') ? p.userDetails : '')
  )
}

/** The app requires both a full name and an email for every user. */
export function isProfileComplete(p: ClientPrincipal): boolean {
  return userName(p).length > 0 && userEmail(p).length > 0
}

/** Record that this user opened the app (name/email from the login) — powers the admin users list. */
export async function markSeen(p: ClientPrincipal): Promise<void> {
  try {
    await fetch('/api/user/seen', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        displayName: userName(p),
        email: userEmail(p),
        identityProvider: p.identityProvider,
        locale: i18n.global.locale.value,
      }),
    })
  } catch {
    /* best-effort, never blocks the UI */
  }
}

export async function getPrincipal(): Promise<ClientPrincipal | null> {
  try {
    const res = await fetch('/.auth/me')
    if (!res.ok) return null
    const data = (await res.json()) as { clientPrincipal: ClientPrincipal | null }
    return data.clientPrincipal ?? null
  } catch {
    return null
  }
}

// Admins sign in with Google too; the administrator role is granted server-side
// by the SWA rolesSource function (/api/GetRoles) based on the account's email.
export const LOGIN_URL = '/.auth/login/google?post_login_redirect_uri=/admin'
export const LOGOUT_URL = '/.auth/logout?post_logout_redirect_uri=/'

/**
 * Log out. Clears the user-specific caches first: the service worker stores /.auth/me and
 * /api/user/* on the device, and a plain SWA logout does not touch Cache Storage — so the
 * next person on a shared phone could otherwise read the previous user's identity and
 * favourites. Navigation happens even if clearing fails, so logout is never blocked.
 */
export async function logout(): Promise<void> {
  try {
    await clearUserData()
  } catch {
    /* best-effort — never trap the user in a signed-in state */
  }
  window.location.assign(LOGOUT_URL)
}

export function isAdmin(principal: ClientPrincipal | null): boolean {
  return principal?.userRoles?.includes('administrator') ?? false
}

// End-user login providers (SWA custom providers configured in staticwebapp.config.json).
export type LoginProvider = 'google' | 'facebook' | 'account'

// Only Google is configured (staticwebapp.config.json). Facebook/e-mail are future providers.
export const PROVIDERS: { id: LoginProvider; label: string }[] = [
  { id: 'google', label: 'Google' },
]

export function loginUrl(provider: LoginProvider, redirect = '/'): string {
  return `/.auth/login/${provider}?post_login_redirect_uri=${encodeURIComponent(redirect)}`
}
